import type { MockedResponse } from "@apollo/client/testing";
import { COMMISSION_FUNDING_BATCHES_QUERY } from "../funding/queries";
import type { CommissionFundingBatchNode } from "../funding/types";
import { commissionFundingBatchFixtures } from "./fundingFixtures";
import { buildConnection } from "./connection";
import { pushActivityEvent } from "./historyMocks";
import { withLineTypenames } from "./linesMocks";
import { createCatalogMock, withTypenames, type MockResolver } from "./mockUtils";
import { runtimeCommissionLines, runtimeFundingBatches, runtimeFundingBatchLines } from "./runtimeState";

function withFundingBatchTypenames(batch: CommissionFundingBatchNode) {
  return {
    __typename: "BackofficeCommissionFundingBatch",
    ...batch,
    partner: withTypenames(batch.partner, "BackofficeCommissionPartner")
  };
}

export function buildFundingBatchesMockData(variables: Record<string, unknown> = {}) {
  const partnerId = variables.partnerId ? String(variables.partnerId) : undefined;
  const batchesById = new Map<string, CommissionFundingBatchNode>();
  for (const batch of commissionFundingBatchFixtures) {
    batchesById.set(batch.id, batch);
  }
  for (const batch of Object.values(runtimeFundingBatches)) {
    batchesById.set(batch.id, batch);
  }

  const nodes = [...batchesById.values()]
    .filter((batch) => !partnerId || batch.partner.id === partnerId)
    .map(withFundingBatchTypenames);

  return {
    commissionFundingBatches: buildConnection("BackofficeCommissionFundingBatchConnection", nodes)
  };
}

function buildFundingBatchMockData(batchId: string) {
  const runtime = runtimeFundingBatches[batchId];
  const fixture = commissionFundingBatchFixtures.find((batch) => batch.id === batchId);
  const batch = runtime ?? fixture;
  if (!batch) {
    return { commissionFundingBatch: null };
  }

  const lines = (runtimeFundingBatchLines[batchId] ?? []).map((line) => ({
    __typename: "BackofficeCommissionFundingBatchLine",
    ...line
  }));
  const summaryLineIds = new Set(lines.map((line) => line.id));
  const fullLines = runtimeCommissionLines.filter((line) => summaryLineIds.has(line.id)).map(withLineTypenames);

  return {
    commissionFundingBatch: {
      ...withFundingBatchTypenames(batch),
      lines,
      linesConnection: buildConnection("BackofficeCommissionLineConnection", fullLines)
    }
  };
}

export const commissionFundingBatchesApolloMock = createCatalogMock(
  COMMISSION_FUNDING_BATCHES_QUERY,
  buildFundingBatchesMockData()
);

export const fundingApolloMocks: MockedResponse[] = [commissionFundingBatchesApolloMock];

export const fundingMockResults: Record<string, Record<string, unknown>> = {
  CommissionFundingBatches: buildFundingBatchesMockData()
};

export const fundingVariableMockResolvers: Record<string, MockResolver> = {
  CommissionFundingBatches: (variables) => buildFundingBatchesMockData(variables),
  CommissionFundingBatch: (variables) => buildFundingBatchMockData(String(variables.id)),
  IssueCommissionFundingBatch: (variables) => {
    const batchId = String(variables.batchId);
    const existing =
      runtimeFundingBatches[batchId] ?? commissionFundingBatchFixtures.find((batch) => batch.id === batchId);
    if (!existing) {
      return { commissionIssueFundingBatch: { success: false, message: "Batch not found." } };
    }

    const issued: CommissionFundingBatchNode = {
      ...existing,
      status: "INVOICED",
      invoicePresentation: "ISSUED",
      issuedOn: new Date().toISOString().slice(0, 10),
      dueOn: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10),
      workdayReference: variables.workdayReference ? String(variables.workdayReference) : `WD-INV-RUNTIME-${batchId}`
    };
    runtimeFundingBatches[batchId] = issued;
    runtimeFundingBatchLines[batchId] = (runtimeFundingBatchLines[batchId] ?? []).map((line) => ({
      ...line,
      fundingStatus: "INVOICED"
    }));
    runtimeCommissionLines.forEach((line, index) => {
      if ((runtimeFundingBatchLines[batchId] ?? []).some((batchLine) => batchLine.id === line.id)) {
        runtimeCommissionLines[index] = { ...line, fundingStatus: "INVOICED" };
      }
    });

    pushActivityEvent(
      "FUNDING_BATCH",
      batchId,
      "ISSUED",
      `Invoice issued (${issued.workdayReference ?? "no reference"}).`
    );

    return {
      commissionIssueFundingBatch: {
        success: true,
        message: "Invoice issued.",
        fundingBatch: withFundingBatchTypenames(issued)
      }
    };
  },
  MarkCommissionFundingBatchPaid: (variables) => {
    const batchId = String(variables.batchId);
    const existing =
      runtimeFundingBatches[batchId] ?? commissionFundingBatchFixtures.find((batch) => batch.id === batchId);
    if (!existing) {
      return { commissionMarkFundingBatchPaid: { success: false, message: "Batch not found." } };
    }

    const paid: CommissionFundingBatchNode = {
      ...existing,
      status: "FUNDED",
      invoicePresentation: "PAID",
      paidOn: variables.paidOn ? String(variables.paidOn) : new Date().toISOString().slice(0, 10)
    };
    runtimeFundingBatches[batchId] = paid;
    runtimeFundingBatchLines[batchId] = (runtimeFundingBatchLines[batchId] ?? []).map((line) => ({
      ...line,
      fundingStatus: "FUNDED"
    }));
    runtimeCommissionLines.forEach((line, index) => {
      if ((runtimeFundingBatchLines[batchId] ?? []).some((batchLine) => batchLine.id === line.id)) {
        runtimeCommissionLines[index] = {
          ...line,
          fundingStatus: "FUNDED",
          payoutEligibilityStatus: line.payoutEligibilityStatus === "HELD" ? "PAYABLE" : line.payoutEligibilityStatus,
          holdReasons: line.holdReasons.filter((reason) => reason !== "awaiting_sponsor_funding")
        };
      }
    });

    pushActivityEvent("FUNDING_BATCH", batchId, "MARK_PAID", "Sponsor payment recorded.");

    return {
      commissionMarkFundingBatchPaid: {
        success: true,
        message: "Batch marked paid.",
        fundingBatch: withFundingBatchTypenames(paid)
      }
    };
  },
  VoidCommissionFundingBatch: (variables) => {
    const batchId = String(variables.batchId);
    const existing =
      runtimeFundingBatches[batchId] ?? commissionFundingBatchFixtures.find((batch) => batch.id === batchId);
    if (!existing) {
      return { commissionVoidFundingBatch: { success: false, message: "Batch not found." } };
    }

    const voided: CommissionFundingBatchNode = {
      ...existing,
      status: "VOID",
      invoicePresentation: "VOID"
    };
    runtimeFundingBatches[batchId] = voided;
    const lineIds = (runtimeFundingBatchLines[batchId] ?? []).map((line) => line.id);
    runtimeCommissionLines.forEach((line, index) => {
      if (lineIds.includes(line.id)) {
        runtimeCommissionLines[index] = { ...line, fundingStatus: "PENDING" };
      }
    });
    runtimeFundingBatchLines[batchId] = [];

    pushActivityEvent("FUNDING_BATCH", batchId, "VOIDED", "Funding batch voided; lines returned to pending.");

    return {
      commissionVoidFundingBatch: {
        success: true,
        message: "Batch voided.",
        fundingBatch: withFundingBatchTypenames(voided)
      }
    };
  }
};
